// Keep dropdowns, popovers and context menus on screen, app-wide.
//
// Menus here are positioned two ways: `position: fixed` from the trigger's
// rect (often portaled), or `position: absolute; top: 100%` inside the
// trigger's container. Most opened downward with no room check, so a menu near
// the bottom of the window ran off it (the Forms list ▾ menu was the report).
// Rather than re-derive placement in every component, a menu opts in with
// `data-fit-viewport`, and this one observer measures it once it renders:
//
//   - runs off the bottom → flip above its anchor if there's room there, else
//     shift it up just enough to fit;
//   - runs off the top / right / left → shift it back inside.
//
// The anchor's top comes from `data-anchor-top` (fixed menus pass the trigger's
// rect.top), or for an absolute menu, its positioned parent. The correction is
// a translate on top of the element's own inline transform, so it works with
// either positioning scheme and never fights React over top/left.
//
// Installed once from index.tsx (installFloatingFit). Re-fits when a tagged
// element mounts or its contents change (options loading in), and on resize
// and scroll.

const SEL = "[data-fit-viewport]";
const MARGIN = 8;
const GAP = 4;

export function fitToViewport(el: HTMLElement): void {
  if (el.dataset.baseTransform === undefined) el.dataset.baseTransform = el.style.transform || "";
  const base = el.dataset.baseTransform;
  el.style.transform = base;
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let dx = 0;
  let dy = 0;

  if (r.bottom > vh - MARGIN) {
    let anchorTop = NaN;
    if (el.dataset.anchorTop !== undefined) anchorTop = Number(el.dataset.anchorTop);
    else if (getComputedStyle(el).position === "absolute" && el.offsetParent instanceof HTMLElement) {
      anchorTop = el.offsetParent.getBoundingClientRect().top;
    }
    const flippedTop = anchorTop - GAP - r.height;
    if (!Number.isNaN(anchorTop) && flippedTop >= MARGIN) dy = flippedTop - r.top;
    else dy = Math.max(MARGIN - r.top, vh - MARGIN - r.bottom);
  } else if (r.top < MARGIN) {
    dy = MARGIN - r.top;
  }
  if (r.right > vw - MARGIN) dx = vw - MARGIN - r.right;
  if (r.left + dx < MARGIN) dx = MARGIN - r.left;

  if (dx || dy) el.style.transform = `${base} translate(${Math.round(dx)}px, ${Math.round(dy)}px)`.trim();
}

let installed = false;

export function installFloatingFit(): void {
  if (installed || typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  installed = true;
  let raf = 0;
  const run = () => document.querySelectorAll<HTMLElement>(SEL).forEach(fitToViewport);
  const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(run); };
  const touches = (n: Node) =>
    n instanceof HTMLElement && (n.matches(SEL) || !!n.querySelector(SEL) || !!n.parentElement?.closest(SEL));
  new MutationObserver(muts => {
    for (const m of muts) {
      if ((m.target instanceof HTMLElement && m.target.closest(SEL)) || Array.from(m.addedNodes).some(touches)) {
        schedule();
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-anchor-top"] });
  window.addEventListener("resize", schedule);
  window.addEventListener("scroll", schedule, true);
}
